import { formatMoney, formatNumber } from './formatMoney';

function sumField(rows, key) {
  return (rows || []).reduce((sum, row) => sum + Number(row[key] || 0), 0);
}

function truncate(text, max = 32) {
  if (!text) return '—';
  const value = String(text).trim();
  return value.length <= max ? value : `${value.slice(0, max)}…`;
}

function shareOf(part, total) {
  if (!total || total <= 0) return null;
  return (Number(part || 0) / total) * 100;
}

function shareLabel(part, total) {
  const share = shareOf(part, total);
  if (share == null) return null;
  return `${share.toFixed(1)}%`;
}

/** One-line decision cue for dashboard pages. */
export function buildOverviewInsight({ stats, products, topEvents }) {
  const leadProduct = [...(products || [])].sort(
    (a, b) => Number(b.totalRevenue || 0) - Number(a.totalRevenue || 0)
  )[0];
  const productTotal = sumField(products, 'totalRevenue');
  const productShare = shareLabel(leadProduct?.totalRevenue, productTotal);
  const leadEvent = (topEvents || [])[0];
  const eventTotal = sumField(topEvents, 'totalRevenue');
  const eventShare = shareLabel(leadEvent?.totalRevenue, eventTotal);

  const revenueStat = (stats || []).find((item) => /revenue/i.test(item.title || ''));
  if (leadEvent && eventShare) {
    const numericShare = shareOf(leadEvent.totalRevenue, eventTotal);
    if (numericShare != null && numericShare >= 20) {
      return {
        tone: 'brand',
        title: `${truncate(leadEvent.eventName, 28)} drives ${eventShare} of ranked event revenue`,
        detail: 'Open Events for the full ranking',
      };
    }
  }
  if (leadProduct && productShare) {
    return {
      tone: 'brand',
      title: `${truncate(leadProduct.productName, 28)} is ${productShare} of product revenue`,
      detail: 'Open Products to compare mix',
    };
  }
  if (revenueStat?.change && revenueStat.change !== '—') {
    return {
      tone: revenueStat.trend === 'down' ? 'warning' : 'brand',
      title: `Revenue ${revenueStat.trend === 'down' ? 'down' : 'up'} ${revenueStat.change} vs prior period`,
      detail: revenueStat.value,
    };
  }
  return {
    tone: 'brand',
    title: 'Snapshot of revenue health for the selected filters',
    detail: 'Use Trends for time series detail',
  };
}

export function buildTrendsInsight({ revenueByDate, aovByDate, currencyCode }) {
  const revenuePoints = revenueByDate || [];
  const aovPoints = aovByDate || [];
  if (revenuePoints.length >= 2) {
    const first = Number(revenuePoints[0]?.totalRevenue || revenuePoints[0]?.value || 0);
    const last = Number(
      revenuePoints[revenuePoints.length - 1]?.totalRevenue
      || revenuePoints[revenuePoints.length - 1]?.value
      || 0
    );
    if (first > 0) {
      const delta = ((last - first) / first) * 100;
      return {
        tone: delta < 0 ? 'warning' : 'brand',
        title: `Revenue ${delta >= 0 ? 'up' : 'down'} ${Math.abs(delta).toFixed(1)}% from first to last point in range`,
        detail: formatMoney(last, { currency: currencyCode }),
      };
    }
  }
  if (aovPoints.length) {
    const lastAov = Number(aovPoints[aovPoints.length - 1]?.aov || aovPoints[aovPoints.length - 1]?.value || 0);
    return {
      tone: 'brand',
      title: `Latest AOV ${formatMoney(lastAov, { currency: currencyCode })}`,
      detail: 'Watch AOV alongside volume on busy fixture weeks',
    };
  }
  return {
    tone: 'brand',
    title: 'Revenue and AOV over time for this filter window',
    detail: null,
  };
}

export function buildProductsInsight(products, currencyCode) {
  const rows = products || [];
  if (!rows.length) return null;
  const total = sumField(rows, 'totalRevenue');
  const sorted = [...rows].sort((a, b) => Number(b.totalRevenue || 0) - Number(a.totalRevenue || 0));
  const top3 = sorted.slice(0, 3);
  const top3Share = shareLabel(sumField(top3, 'totalRevenue'), total);
  const lead = sorted[0];
  return {
    tone: 'brand',
    title: `Top 3 products = ${top3Share || '—'} of product revenue`,
    detail: lead
      ? `Lead: ${truncate(lead.productName)} · ${formatMoney(lead.totalRevenue, { currency: currencyCode })}`
      : null,
  };
}

export function buildEventsInsight(rows, currencyCode) {
  if (!rows?.length) return null;
  const total = sumField(rows, 'totalRevenue');
  const lead = rows[0];
  const share = shareOf(lead?.totalRevenue, total);
  const shareText = share == null ? null : `${share.toFixed(1)}%`;
  return {
    tone: share != null && share >= 25 ? 'warning' : 'brand',
    title: shareText == null
      ? `${truncate(lead.eventName)} leads event revenue`
      : `${truncate(lead.eventName)} = ${shareText} of ranked event revenue`,
    detail: formatMoney(lead.totalRevenue, { currency: currencyCode }),
  };
}

export function buildVenuesInsight(venues, currencyCode) {
  if (!venues?.length) return null;
  const total = sumField(venues, 'totalRevenue');
  const lead = venues[0];
  const share = shareOf(lead?.totalRevenue, total);
  const shareText = share == null ? null : `${share.toFixed(1)}%`;
  return {
    tone: share != null && share >= 40 ? 'warning' : 'brand',
    title: shareText == null
      ? `${truncate(lead.venueName)} leads venue revenue`
      : `${truncate(lead.venueName)} = ${shareText} of ranked venue revenue`,
    detail: `${formatNumber(venues.length)} venues · ${formatMoney(total, { currency: currencyCode })} total`,
  };
}

export function buildConcentrationRows(rows, nameKey, valueKey = 'totalRevenue', limit = 5) {
  const list = Array.isArray(rows) ? rows : [];
  const total = sumField(list, valueKey);
  return list.slice(0, limit).map((row, index) => {
    const value = Number(row[valueKey] || 0);
    return {
      id: row.id || `${nameKey}-${index}`,
      name: row[nameKey] || 'Unknown',
      value,
      share: shareOf(value, total) || 0,
    };
  });
}
